import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EMPTY, Subject, catchError, forkJoin, switchMap, tap } from 'rxjs';

import { SortDirectionType } from '../../../core/models/sort-direction';
import { PatientPortalProxyUserRequest } from '../../../requests/models/patient-portal-proxy-user-request';
import { PatientPortalUserRequest } from '../../../requests/models/patient-portal-user-request';
import {
  PatientPortalProxyUserRequestsRequest,
  PatientPortalProxyUserRequestsService,
} from '../../../requests/services/patient-portal-proxy-user-requests.service';
import {
  PatientPortalUserRequestsRequest,
  PatientPortalUserRequestsService,
} from '../../../requests/services/patient-portal-user-requests.service';
import {
  getIdentifierAriaLabel,
  getVisibleIdentifier,
} from '../../../requests/utils/identifier.utils';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import {
  formatDobNumeric,
  formatRelativeTime,
  parseUtc,
} from '../../../shared/utils/date-time/date-time.utils';
import { createClientPager } from '../../../shared/utils/pagination/pagination.utils';
import {
  RequestStatusKind,
  getProxyStatusBadge,
  getStatusBadge,
} from '../../../shared/utils/status-badge/status-badge.utils';
import { EMPTY_CELL, buildQueueRowAriaLabel } from '../../../shared/utils/table/table.utils';
import { DashboardSummary } from '../../models/dashboard-summary';
import { DashboardService } from '../../services/dashboard.service';

interface RecentRequest {
  id: number;
  submittedAt: Date;
  patientName: string;
  dob: string;
  identifier: string;
  identifierAriaLabel: string;
  ariaLabel: string;
  assignedToDisplayName: string | null;
  statusLabel: string;
  statusKind: RequestStatusKind;
}

interface RecentProxyRequest {
  id: number;
  submittedAt: Date;
  patientName: string;
  caregiverName: string;
  relationship: string;
  ariaLabel: string;
  assignedToDisplayName: string | null;
  statusLabel: string;
  statusKind: RequestStatusKind;
}

@Component({
  selector: 'app-dashboard-overview',
  standalone: true,
  imports: [IconComponent, ProgressSpinnerComponent],
  templateUrl: './dashboard-overview.html',
  styleUrls: ['./dashboard-overview.scss'],
})
export class DashboardOverview implements OnInit {
  /**
   * Upper bound on rows fetched per queue for the recent tables (newest
   * first). Headline counts come from the server summary endpoint, so this
   * only caps how far back the client-side pager can page — keep it small to
   * avoid pulling PHI the dashboard never shows.
   */
  private static readonly REQUESTS_PAGE_SIZE = 50;
  /** Rows shown per page in the dashboard tables. */
  private static readonly RECENT_REQUESTS_PAGE_SIZE = 5;

  private readonly destroyRef = inject(DestroyRef);
  private readonly requestsService = inject(PatientPortalUserRequestsService);
  private readonly proxyRequestsService = inject(PatientPortalProxyUserRequestsService);
  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);
  private readonly loadRequestsTrigger$ = new Subject<void>();

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly allRequests = signal<PatientPortalUserRequest[]>([]);
  readonly allProxyRequests = signal<PatientPortalProxyUserRequest[]>([]);
  /** Server-side headline counts for the summary cards. */
  readonly summary = signal<DashboardSummary | null>(null);

  readonly unreviewedCount = computed(() => this.summary()?.unreviewedCount ?? 0);
  readonly inProgressCount = computed(() => this.summary()?.inProgressCount ?? 0);
  readonly needsInfoCount = computed(() => this.summary()?.needsInfoCount ?? 0);
  readonly overdueCount = computed(() => this.summary()?.overdueCount ?? 0);
  readonly proxyVerificationPendingCount = computed(
    () => this.summary()?.proxyVerificationPendingCount ?? 0,
  );
  readonly assignedToMeCount = computed(() => this.summary()?.assignedToMeCount ?? 0);

  readonly recentRequests = computed(() =>
    this.allRequests().map((request) => this.mapRequest(request)),
  );

  readonly recentProxyRequests = computed(() =>
    this.allProxyRequests().map((request) => this.mapProxyRequest(request)),
  );

  private readonly requestsPager = createClientPager(
    this.recentRequests,
    DashboardOverview.RECENT_REQUESTS_PAGE_SIZE,
    'requests',
  );
  private readonly proxyPager = createClientPager(
    this.recentProxyRequests,
    DashboardOverview.RECENT_REQUESTS_PAGE_SIZE,
    'verifications',
  );

  readonly currentPage = this.requestsPager.currentPage;
  readonly totalPages = this.requestsPager.totalPages;
  readonly paginatedRequests = this.requestsPager.pagedItems;
  readonly pageItems = this.requestsPager.pageItems;
  readonly showingSummary = this.requestsPager.showingSummary;

  readonly currentProxyPage = this.proxyPager.currentPage;
  readonly proxyTotalPages = this.proxyPager.totalPages;
  readonly paginatedProxyRequests = this.proxyPager.pagedItems;
  readonly proxyPageItems = this.proxyPager.pageItems;
  readonly proxyShowingSummary = this.proxyPager.showingSummary;

  ngOnInit(): void {
    this.loadRequestsTrigger$
      .pipe(
        tap(() => {
          this.isLoading.set(true);
          this.loadError.set(null);
        }),
        switchMap(() =>
          forkJoin({
            linking: this.requestsService.getRequests(this.buildRequestsPayload()),
            proxy: this.proxyRequestsService.getRequests(this.buildProxyRequestsPayload()),
            summary: this.dashboardService.getSummary(),
          }).pipe(
            catchError(() => {
              this.loadError.set('Unable to load the dashboard right now.');
              this.allRequests.set([]);
              this.allProxyRequests.set([]);
              this.summary.set(null);
              this.isLoading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ linking, proxy, summary }) => {
          this.allRequests.set(linking.requests);
          this.allProxyRequests.set(proxy.requests);
          this.summary.set(summary);
          this.requestsPager.reset();
          this.proxyPager.reset();
          this.isLoading.set(false);
        },
        // HTTP failures are absorbed by catchError inside switchMap; this only
        // fires if the stream itself dies (e.g. a payload builder throws), and
        // without it that death would be silent while the spinner keeps going.
        error: () => {
          this.loadError.set('Unable to load the dashboard right now.');
          this.isLoading.set(false);
        },
      });

    this.loadRequestsTrigger$.next();
  }

  goToPage(page: number): void {
    this.requestsPager.goToPage(page);
  }

  goToProxyPage(page: number): void {
    this.proxyPager.goToPage(page);
  }

  viewFullQueue(): void {
    void this.router.navigate(['/requests']);
  }

  viewFullProxyQueue(): void {
    void this.router.navigate(['/requests/proxy-verifications']);
  }

  retry(): void {
    this.loadRequestsTrigger$.next();
  }

  openRequest(requestId: number): void {
    void this.router.navigate(['/request-detail', requestId]);
  }

  onRowKeydown(event: KeyboardEvent, requestId: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openRequest(requestId);
    }
  }

  openProxyRequest(requestId: number): void {
    // Same path the Proxy Queue rows use — the detail screen is routed under the
    // queue, so a bare /proxy-request-detail/:id falls through to the wildcard.
    void this.router.navigate(['/requests/proxy-verifications', requestId]);
  }

  onProxyRowKeydown(event: KeyboardEvent, requestId: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openProxyRequest(requestId);
    }
  }

  getRelativeSubmittedAt(request: RecentRequest | RecentProxyRequest): string {
    return formatRelativeTime(request.submittedAt);
  }

  private mapRequest(request: PatientPortalUserRequest): RecentRequest {
    const badge = getStatusBadge(request.workflowStatusTypeId);
    const dob = formatDobNumeric(request.dateOfBirth);
    const identifierAriaLabel = getIdentifierAriaLabel(request);

    return {
      id: request.patientPortalUserRequestId,
      submittedAt: parseUtc(request.createdOnUtc),
      patientName: request.patientDisplayName ?? EMPTY_CELL,
      dob,
      identifier: getVisibleIdentifier(request),
      identifierAriaLabel,
      // The row's announcement must carry the columns, not just the name — see
      // buildQueueRowAriaLabel. A request with no submitted name drops the name
      // clause instead of reading out the cell's em-dash placeholder.
      ariaLabel: buildQueueRowAriaLabel(
        request.patientDisplayName
          ? `Open request for ${request.patientDisplayName}`
          : 'Open request',
        [
          dob ? `date of birth ${dob}` : null,
          identifierAriaLabel,
          request.assignedToDisplayName
            ? `assigned to ${request.assignedToDisplayName}`
            : 'unassigned',
          `status ${badge.label}`,
        ],
      ),
      assignedToDisplayName: request.assignedToDisplayName,
      statusLabel: badge.label,
      statusKind: badge.kind,
    };
  }

  private mapProxyRequest(request: PatientPortalProxyUserRequest): RecentProxyRequest {
    const badge = getProxyStatusBadge(request.workflowStatusTypeId);
    // The proxy contract declares patientName non-nullable, but the linking
    // queue's patientDisplayName made the same claim and shipped nulls. Until
    // the API team confirms this field, guard it the same way rather than
    // rendering a blank primary cell and announcing "Open proxy request for ".
    const patientName = request.patientName || EMPTY_CELL;

    return {
      id: request.patientPortalProxyUserRequestId,
      submittedAt: parseUtc(request.createdOnUtc),
      patientName,
      caregiverName: request.name,
      relationship: request.relationshipToPatient,
      ariaLabel: buildQueueRowAriaLabel(
        request.patientName
          ? `Open proxy request for ${request.patientName}`
          : 'Open proxy request',
        [
          request.name ? `caregiver ${request.name}` : null,
          request.relationshipToPatient ? `relationship ${request.relationshipToPatient}` : null,
          request.assignedToDisplayName
            ? `assigned to ${request.assignedToDisplayName}`
            : 'unassigned',
          `status ${badge.label}`,
        ],
      ),
      assignedToDisplayName: request.assignedToDisplayName,
      statusLabel: badge.label,
      statusKind: badge.kind,
    };
  }

  private buildRequestsPayload(): PatientPortalUserRequestsRequest {
    return {
      createdAfterUtc: null,
      workflowStatusTypeId: null,
      assignedToPatientPortalAdminUserId: null,
      unassignedOnly: false,
      pageNumber: 1,
      pageSize: DashboardOverview.REQUESTS_PAGE_SIZE,
      searchTerm: null,
      sortColumn: 'CreatedOnUtc',
      sortDirection: SortDirectionType.Descending,
    };
  }

  private buildProxyRequestsPayload(): PatientPortalProxyUserRequestsRequest {
    return {
      createdAfterUtc: null,
      searchTerm: null,
      relationshipToPatient: null,
      workflowStatusTypeId: null,
      assignedToPatientPortalAdminUserId: null,
      unassignedOnly: false,
      sortDirection: SortDirectionType.Descending,
      sortColumn: 'CreatedOnUtc',
      pageNumber: 1,
      pageSize: DashboardOverview.REQUESTS_PAGE_SIZE,
    };
  }
}
